/**
 * 🧑‍🎨 PERFIL DEL COLECCIONISTA — lógica PURA del módulo perfil (Wave 1 ·
 * W1-PROFILE). Sin dependencias de la DB: reglas de selección de destacados,
 * marcos estacionales, título derivado y — sobre todo — la RESOLUCIÓN DE
 * PRIVACIDAD («un perfil privado no filtra»). Todo testeable en aislamiento.
 */

/** Tope de figuras destacadas en el perfil (diseño `_con_destacados`). */
export const MAX_FEATURED = 5;

/** Visibilidad de un bloque de contenido (colección / deseos). */
export type ProfileVisibility = "everyone" | "followers" | "private";

/** Permiso de interacción (comentarios / etiquetado). */
export type InteractionPermission = "everyone" | "followers" | "nobody";

/** Marcos estacionales decorativos alrededor del avatar. */
export type FrameId = "autumn" | "spring" | "winter" | "summer";

export const FRAME_IDS: readonly FrameId[] = ["autumn", "spring", "winter", "summer"] as const;

export function isFrameId(value: string | null | undefined): value is FrameId {
  return value != null && (FRAME_IDS as readonly string[]).includes(value);
}

/** Ajustes de privacidad + presentación de un perfil (espejo de `collector_profile`). */
export interface CollectorProfileSettings {
  display_name?: string | null;
  bio?: string | null;
  frame_id?: string | null;
  is_private?: boolean | number | null;
  collection_visibility?: string | null;
  wishlist_visibility?: string | null;
  show_location?: boolean | number | null;
  comment_permission?: string | null;
  tag_permission?: string | null;
}

/** Defaults idénticos a los de la migración 00053 (perfil sin configurar). */
export const DEFAULT_PROFILE_SETTINGS = {
  display_name: null,
  bio: null,
  frame_id: null,
  is_private: false,
  collection_visibility: "everyone" as ProfileVisibility,
  wishlist_visibility: "followers" as ProfileVisibility,
  show_location: false,
  comment_permission: "everyone" as InteractionPermission,
  tag_permission: "followers" as InteractionPermission,
} as const;

/** Normaliza el 0/1 de SQLite (o bool) a boolean. */
export function asBool(value: boolean | number | null | undefined): boolean {
  return value === true || value === 1;
}

// ─── Destacados (máx. 5) ────────────────────────────────────────────────────

/** ¿Se puede destacar una figura más? (tope duro de {@link MAX_FEATURED}). */
export function canAddFeatured(currentCount: number): boolean {
  return currentCount < MAX_FEATURED;
}

export interface FeaturedToggleResult {
  /** Nueva selección de product_ids. */
  ids: string[];
  /** true si la operación modificó la selección. */
  changed: boolean;
  /** true si se rechazó AÑADIR por estar en el tope. */
  atCap: boolean;
  /** true si el producto quedó destacado tras la operación. */
  featured: boolean;
}

/**
 * Alterna un producto en la lista de destacados respetando el tope de 5. Si ya
 * está → lo quita (siempre permitido). Si no está y hay hueco → lo añade. Si no
 * está y está en el tope → NO cambia nada y marca `atCap` (la UI avisa). PURA.
 */
export function toggleFeaturedSelection(
  currentIds: readonly string[],
  productId: string,
): FeaturedToggleResult {
  if (currentIds.includes(productId)) {
    return {
      ids: currentIds.filter((id) => id !== productId),
      changed: true,
      atCap: false,
      featured: false,
    };
  }
  if (!canAddFeatured(currentIds.length)) {
    return { ids: [...currentIds], changed: false, atCap: true, featured: false };
  }
  return { ids: [...currentIds, productId], changed: true, atCap: false, featured: true };
}

// ─── Título del coleccionista (fallback local del badge de gamificación) ─────

/**
 * Título mostrado bajo el avatar (el «EXPERT» del diseño). Se DERIVA del tamaño
 * de la colección como fallback local-first: el rango real lo publica el módulo
 * de gamificación (Agente E) por su reducer; hasta que su superficie esté
 * disponible, este título mantiene el perfil vivo sin acoplarse a ese módulo.
 */
export function deriveCollectorTitle(figureCount: number): string {
  if (figureCount >= 250) {
    return "Maestro";
  }
  if (figureCount >= 100) {
    return "Experto";
  }
  if (figureCount >= 40) {
    return "Veterano";
  }
  if (figureCount >= 10) {
    return "Aficionado";
  }
  if (figureCount >= 1) {
    return "Novato";
  }
  return "Recién llegado";
}

// ─── Privacidad: «un perfil privado no filtra» ──────────────────────────────

export interface ViewerContext {
  /** El espectador es el dueño del perfil. */
  isOwner: boolean;
  /** El espectador sigue (aprobado) al dueño. */
  isFollower: boolean;
}

function allowsVisibility(visibility: string | null | undefined, ctx: ViewerContext): boolean {
  if (ctx.isOwner) {
    return true;
  }
  switch (visibility) {
    case "everyone": {
      return true;
    }
    case "followers": {
      return ctx.isFollower;
    }
    default: {
      // "private" o valor desconocido → sólo el dueño.
      return false;
    }
  }
}

/**
 * ¿Puede el espectador ver el perfil? El dueño siempre. Un perfil PRIVADO
 * (`is_private`) sólo lo ven los seguidores aprobados; uno público, cualquiera.
 */
export function canViewProfile(profile: CollectorProfileSettings, ctx: ViewerContext): boolean {
  if (ctx.isOwner) {
    return true;
  }
  return asBool(profile.is_private) ? ctx.isFollower : true;
}

/** ¿Puede ver la COLECCIÓN? Gate de perfil + visibilidad de colección. */
export function canViewCollection(profile: CollectorProfileSettings, ctx: ViewerContext): boolean {
  return canViewProfile(profile, ctx) && allowsVisibility(profile.collection_visibility, ctx);
}

/** ¿Puede ver los DESEOS (wishlist)? Gate de perfil + visibilidad de deseos. */
export function canViewWishlist(profile: CollectorProfileSettings, ctx: ViewerContext): boolean {
  return canViewProfile(profile, ctx) && allowsVisibility(profile.wishlist_visibility, ctx);
}

/** ¿Puede COMENTAR al dueño? Gate de perfil + permiso de comentarios. */
export function canComment(profile: CollectorProfileSettings, ctx: ViewerContext): boolean {
  if (ctx.isOwner) {
    return false;
  } // uno no se comenta a sí mismo en este gate
  if (!canViewProfile(profile, ctx)) {
    return false;
  }
  switch (profile.comment_permission) {
    case "everyone": {
      return true;
    }
    case "followers": {
      return ctx.isFollower;
    }
    default: {
      return false;
    }
  }
}
